import { useCallback, useEffect, useRef, useState } from 'react'
import Stack from '@mui/joy/Stack'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import Button from '@mui/joy/Button'
import Input from '@mui/joy/Input'
import Alert from '@mui/joy/Alert'
import Divider from '@mui/joy/Divider'
import Chip from '@mui/joy/Chip'
import CircularProgress from '@mui/joy/CircularProgress'
import AccordionGroup from '@mui/joy/AccordionGroup'
import Accordion from '@mui/joy/Accordion'
import AccordionDetails from '@mui/joy/AccordionDetails'
import AccordionSummary from '@mui/joy/AccordionSummary'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import FormHelperText from '@mui/joy/FormHelperText'
import SettingsIcon from '@mui/icons-material/Settings'
import GroupsIcon from '@mui/icons-material/Groups'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'

import { type DsGmeetWorkspaceConfig, getWorkspaceConfig, updateWorkspaceConfig } from '../../api/dsGmeetApi'

// ─── chip-list editor ─────────────────────────────────────────────────────────

function ChipListEditor({
    label,
    helperText,
    values,
    onChange,
    placeholder,
}: {
    label: string
    helperText?: string
    values: string[]
    onChange: (next: string[]) => void
    placeholder?: string
}) {
    const [draft, setDraft] = useState('')
    const inputRef = useRef<HTMLInputElement>(null)

    const add = () => {
        const v = draft.trim().toLowerCase()
        if (v && !values.includes(v)) onChange([...values, v])
        setDraft('')
        inputRef.current?.focus()
    }

    return (
        <FormControl>
            <FormLabel>{label}</FormLabel>
            <Stack gap={1}>
                <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ minHeight: 32 }}>
                    {values.map((v) => (
                        <Chip
                            key={v}
                            size="sm"
                            variant="soft"
                            endDecorator={
                                <CloseIcon fontSize="small" sx={{ cursor: 'pointer', ml: 0.25 }} onClick={() => onChange(values.filter((x) => x !== v))} />
                            }
                        >
                            {v}
                        </Chip>
                    ))}
                    {values.length === 0 && (
                        <Typography level="body-xs" color="neutral" sx={{ alignSelf: 'center' }}>
                            (none)
                        </Typography>
                    )}
                </Stack>
                <Stack direction="row" gap={0.75}>
                    <Input
                        size="sm"
                        placeholder={placeholder ?? 'Add value…'}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault()
                                add()
                            }
                        }}
                        slotProps={{ input: { ref: inputRef } }}
                        sx={{ flex: 1 }}
                    />
                    <Button size="sm" variant="outlined" startDecorator={<AddIcon />} onClick={add} disabled={!draft.trim()}>
                        Add
                    </Button>
                </Stack>
            </Stack>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    )
}

// ─── workspace config form ────────────────────────────────────────────────────

function WorkspaceConfigForm() {
    const [loaded, setLoaded] = useState<DsGmeetWorkspaceConfig | null>(null)
    const [form, setForm] = useState<DsGmeetWorkspaceConfig | null>(null)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [saved, setSaved] = useState(false)

    const fetchConfig = useCallback(async () => {
        setLoading(true)
        setError('')
        try {
            const cfg = await getWorkspaceConfig()
            setLoaded(cfg)
            setForm(cfg)
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        void fetchConfig()
    }, [fetchConfig])

    const dirty = form && loaded ? JSON.stringify(form) !== JSON.stringify(loaded) : false

    const save = async () => {
        if (!form) return
        setSaving(true)
        setError('')
        setSaved(false)
        try {
            const updated = await updateWorkspaceConfig(form)
            setLoaded(updated)
            setForm(updated)
            setSaved(true)
            setTimeout(() => setSaved(false), 3000)
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err))
        } finally {
            setSaving(false)
        }
    }

    if (loading && !form) return <CircularProgress size="sm" />

    return (
        <Stack gap={2}>
            {error && (
                <Alert color="danger" size="sm">
                    {error}
                </Alert>
            )}
            {saved && (
                <Alert color="success" size="sm">
                    Settings saved.
                </Alert>
            )}

            {form && (
                <>
                    <Sheet variant="soft" color="primary" sx={{ borderRadius: 'sm', p: 1.5 }}>
                        <Typography level="title-sm" sx={{ mb: 0.5 }}>
                            When does the bot join?
                        </Typography>
                        <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
                            A calendar event is recorded only if <strong>all</strong> conditions below are true. Otherwise the bot does not join and the reason
                            is written to the server logs (search <code>calendar qualify: WILL NOT JOIN</code>).
                        </Typography>
                        <ol style={{ margin: 0, paddingLeft: '1.25rem' }}>
                            <li>
                                <Typography level="body-sm">Event has a video meeting URL (Google Meet, Zoom, Teams)</Typography>
                            </li>
                            <li>
                                <Typography level="body-sm">Event is in the future</Typography>
                            </li>
                            <li>
                                <Typography level="body-sm">
                                    Calendar owner has role <code>SALES</code> or <code>SALES_LEAD</code>
                                </Typography>
                            </li>
                            <li>
                                <Typography level="body-sm">Event title contains none of the configured skip markers</Typography>
                            </li>
                            <li>
                                <Typography level="body-sm">
                                    At least one attendee is on an <em>external</em> email domain
                                </Typography>
                            </li>
                        </ol>
                    </Sheet>

                    <Divider />

                    <ChipListEditor
                        label="Internal email domains"
                        helperText="Attendees with these domains are treated as internal staff and excluded from the external-attendee check. Use lowercase, no @ prefix."
                        values={form.internalEmailDomains}
                        onChange={(v) => setForm((f) => (f ? { ...f, internalEmailDomains: v } : f))}
                        placeholder="numeo.ai"
                    />

                    <Divider />

                    <ChipListEditor
                        label="Skip title markers"
                        helperText="Events whose title contains any of these strings (case-insensitive) will not be recorded."
                        values={form.skipTitleMarkers}
                        onChange={(v) => setForm((f) => (f ? { ...f, skipTitleMarkers: v } : f))}
                        placeholder="[no-record]"
                    />

                    <Divider />

                    <Stack direction="row" gap={2} flexWrap="wrap">
                        <FormControl sx={{ maxWidth: 220 }}>
                            <FormLabel>Bot join lead time</FormLabel>
                            <Input
                                type="number"
                                size="sm"
                                value={form.botJoinLeadMinutes}
                                slotProps={{ input: { min: 0, max: 30 } }}
                                onChange={(e) => {
                                    const v = parseInt(e.target.value, 10)
                                    if (!isNaN(v)) setForm((f) => (f ? { ...f, botJoinLeadMinutes: v } : f))
                                }}
                                endDecorator={<Typography level="body-xs">min</Typography>}
                            />
                            <FormHelperText>How early the bot tries to join (Recall: &ge;10 min = guaranteed scheduled, &lt;10 = ad-hoc).</FormHelperText>
                        </FormControl>

                        <FormControl sx={{ maxWidth: 220 }}>
                            <FormLabel>Bot retention hours</FormLabel>
                            <Input
                                type="number"
                                size="sm"
                                value={form.botRetentionHours}
                                slotProps={{ input: { min: 1, max: 720 } }}
                                onChange={(e) => {
                                    const v = parseInt(e.target.value, 10)
                                    if (!isNaN(v)) setForm((f) => (f ? { ...f, botRetentionHours: v } : f))
                                }}
                                endDecorator={<Typography level="body-xs">h</Typography>}
                            />
                            <FormHelperText>Recall side safety-net. Max 720 h (30 days).</FormHelperText>
                        </FormControl>
                    </Stack>
                </>
            )}

            <Stack direction="row" gap={1}>
                <Button size="sm" loading={saving} disabled={!dirty || saving} onClick={save}>
                    Save changes
                </Button>
                {dirty && (
                    <Button
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={() => {
                            if (loaded) setForm(loaded)
                        }}
                    >
                        Discard
                    </Button>
                )}
            </Stack>

            <Sheet variant="soft" sx={{ borderRadius: 'sm', p: 1.5 }}>
                <Typography level="body-xs" color="neutral">
                    <strong>GCS bucket:</strong> numeo-calls &nbsp;·&nbsp; <strong>Recall API:</strong> https://us-west-2.recall.ai &nbsp;·&nbsp; These values
                    are infra-locked (set at workspace creation) and not editable here.
                </Typography>
            </Sheet>
        </Stack>
    )
}

// ─── main component ───────────────────────────────────────────────────────────

export function SettingsTab() {
    return (
        <Stack gap={1.5}>
            <Typography level="title-sm">Settings</Typography>
            <Typography level="body-xs" color="neutral">
                Workspace-level recording configuration and sales team roster.
            </Typography>

            <AccordionGroup variant="outlined" sx={{ borderRadius: 'sm' }}>
                <Accordion defaultExpanded>
                    <AccordionSummary>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <SettingsIcon fontSize="small" />
                            <Typography level="title-sm">Workspace configuration</Typography>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                        <WorkspaceConfigForm />
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <GroupsIcon fontSize="small" />
                            <Typography level="title-sm">Sales team roster</Typography>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack gap={1.25}>
                            <Alert color="warning" size="sm">
                                Role assignment (SALES / SALES_LEAD / OTHER) is managed in admin-panel's users module. Once main-service publishes a write
                                endpoint this section will gain inline toggles.
                            </Alert>
                            <ol style={{ marginTop: 0, paddingLeft: '1.25rem' }}>
                                <li>
                                    <Typography level="body-sm">Change a user's role in admin-panel.</Typography>
                                </li>
                                <li>
                                    <Typography level="body-sm">Qualification re-evaluates on the next Recall calendar webhook.</Typography>
                                </li>
                            </ol>
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            </AccordionGroup>
        </Stack>
    )
}
